import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'
import { peekPendingIntent, setPendingIntent } from './pendingIntent'
import type {
  InviteCodePreview,
  JoinGroupRequest,
  JoinGroupResult,
  ResolveInviteCodeResult,
  RotateInviteCodeRequest,
  RotateInviteCodeResult,
} from '@shared/groupJoin'

const callJoinGroup = httpsCallable<JoinGroupRequest, JoinGroupResult>(functions, 'joinGroup')
const callResolveInviteCode = httpsCallable<{ code: string }, ResolveInviteCodeResult>(
  functions,
  'resolveInviteCode',
)
const callRotateInviteCode = httpsCallable<RotateInviteCodeRequest, RotateInviteCodeResult>(
  functions,
  'rotateGroupInviteCode',
)

// Joining is server-side: the group rules don't let a client add itself, so
// every join carries proof of an invitation (see shared/groupJoin.ts).
export async function joinGroup(request: JoinGroupRequest): Promise<JoinGroupResult> {
  const { data } = await callJoinGroup(request)
  return data
}

export async function resolveInviteCode(code: string): Promise<InviteCodePreview | null> {
  const { data } = await callResolveInviteCode({ code: code.toUpperCase() })
  return data.group
}

export async function rotateGroupInviteCode(groupId: string): Promise<string> {
  const { data } = await callRotateInviteCode({ groupId })
  return data.inviteCode
}

export type PendingGroupJoin = JoinGroupRequest & { groupId: string }

// A guest who signs into an existing account gets a new UID, so the join they
// started has to carry its proof across sign-in. Older builds stored a bare
// group id; that still parses, and still works if they were already a guest.
export function setPendingGroupJoin(join: PendingGroupJoin): void {
  setPendingIntent('groupJoin', JSON.stringify(join))
}

export function peekPendingGroupJoin(): PendingGroupJoin | null {
  const raw = peekPendingIntent('groupJoin')
  if (!raw) return null
  if (!raw.startsWith('{')) return { groupId: raw }
  try {
    const parsed = JSON.parse(raw) as PendingGroupJoin
    return typeof parsed.groupId === 'string' ? parsed : null
  } catch {
    return null
  }
}
